import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import { useState } from "react"
import { useStore } from "../store/useStore"

type Preset = "none" | "1m" | "3m" | "custom"

function subtractMonths(months: number): string {
  const d = new Date()
  d.setMonth(d.getMonth() - months)
  return d.toISOString().slice(0, 10)
}

export function FreshnessDatePicker() {
  const { translate } = useTranslation()
  const freshnessDate = useStore((s) => s.freshnessDate)
  const setFreshnessDate = useStore((s) => s.setFreshnessDate)

  const oneMonthAgo = subtractMonths(1)
  const threeMonthsAgo = subtractMonths(3)

  const activePreset: Preset =
    freshnessDate === null
      ? "none"
      : freshnessDate === oneMonthAgo
        ? "1m"
        : freshnessDate === threeMonthsAgo
          ? "3m"
          : "custom"

  const [showCustom, setShowCustom] = useState(activePreset === "custom")

  const handlePreset = (preset: Preset) => {
    if (preset === "none") {
      setFreshnessDate(null)
      setShowCustom(false)
    } else if (preset === "1m") {
      setFreshnessDate(oneMonthAgo)
      setShowCustom(false)
    } else if (preset === "3m") {
      setFreshnessDate(threeMonthsAgo)
      setShowCustom(false)
    } else {
      setShowCustom(true)
      if (!freshnessDate) setFreshnessDate(oneMonthAgo)
    }
  }

  const btnClass = (active: boolean) =>
    clsx(
      "rounded-lg px-3 py-1.5 text-xs font-medium transition-colors",
      active
        ? "bg-yellow1 text-black"
        : "border border-grey4 bg-grey2 text-grey5 hover:bg-grey3 hover:text-grey6",
    )

  return (
    <div className="flex flex-col gap-2">
      <label className="text-xs font-medium text-grey5">
        {translate("QMS/FRESHNESS/FILTER")}
      </label>
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => handlePreset("none")}
          className={btnClass(activePreset === "none")}
        >
          {translate("QMS/FRESHNESS/NO_FILTER")}
        </button>
        <button
          onClick={() => handlePreset("1m")}
          className={btnClass(activePreset === "1m")}
        >
          {translate("QMS/FRESHNESS/ONE_MONTH")}
        </button>
        <button
          onClick={() => handlePreset("3m")}
          className={btnClass(activePreset === "3m")}
        >
          {translate("QMS/FRESHNESS/THREE_MONTHS")}
        </button>
        <button
          onClick={() => handlePreset("custom")}
          className={btnClass(activePreset === "custom")}
        >
          {translate("QMS/FRESHNESS/CUSTOM")}
        </button>
      </div>
      {showCustom && (
        <input
          type="date"
          value={freshnessDate ?? ""}
          onChange={(e) => {
            if (e.target.value) setFreshnessDate(e.target.value)
          }}
          className="w-44 rounded-lg border border-grey4 bg-grey2 px-3 py-1.5 text-xs text-white focus:outline-hidden focus:ring-1 focus:ring-yellow1"
        />
      )}
      {freshnessDate ? (
        <p className="text-[10px] leading-tight text-grey5">
          {translate("QMS/FRESHNESS/CHECKED_HELP_PREFIX")}{" "}
          <span className="font-mono text-yellow1">{freshnessDate}</span>{" "}
          {translate("QMS/FRESHNESS/CHECKED_HELP_MIDDLE")}{" "}
          <span className="font-semibold text-success">
            {translate("QMS/FRESHNESS/GREEN")}
          </span>
          , {translate("QMS/FRESHNESS/BEFORE")}{" "}
          <span className="font-semibold text-yellow1">
            {translate("QMS/FRESHNESS/YELLOW")}
          </span>
          , {translate("QMS/FRESHNESS/EMPTY_ARE")}{" "}
          <span className="font-semibold text-red1">
            {translate("QMS/FRESHNESS/RED")}
          </span>
          .
        </p>
      ) : (
        <p className="text-[10px] leading-tight text-grey5">
          {translate("QMS/FRESHNESS/UNCHECKED_HELP")}
        </p>
      )}
    </div>
  )
}
